import { b2Scalar } from "./precision.js";

export class b2Vec2 {
  constructor(x = 0, y = 0) {
    this.x = b2Scalar(x);
    this.y = b2Scalar(y);
  }

  static Make(x, y) {
    return new b2Vec2(x, y);
  }

  Add(v) {
    this.x = b2Scalar(this.x + v.x);
    this.y = b2Scalar(this.y + v.y);
  }

  Set(x = 0, y = 0) {
    this.x = b2Scalar(x);
    this.y = b2Scalar(y);
  }

  SetV(v) {
    this.x = b2Scalar(v.x);
    this.y = b2Scalar(v.y);
  }

  SetZero() {
    this.x = 0;
    this.y = 0;
  }

  Multiply(value) {
    this.x = b2Scalar(this.x * value);
    this.y = b2Scalar(this.y * value);
  }

  Copy() {
    return new b2Vec2(this.x, this.y);
  }

  Length() {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  LengthSquared() {
    return this.x * this.x + this.y * this.y;
  }

  Normalize() {
    const length = Math.sqrt(this.x * this.x + this.y * this.y);
    if (length < Number.MIN_VALUE) {
      return 0;
    }
    const inv = b2Scalar(1 / length);
    this.x = b2Scalar(this.x * inv);
    this.y = b2Scalar(this.y * inv);
    return b2Scalar(length);
  }

  Negative() {
    return new b2Vec2(-this.x, -this.y);
  }

  Subtract(v) {
    this.x = b2Scalar(this.x - v.x);
    this.y = b2Scalar(this.y - v.y);
  }

  MinV(v) {
    this.x = this.x < v.x ? this.x : v.x;
    this.y = this.y < v.y ? this.y : v.y;
  }

  MaxV(v) {
    this.x = this.x > v.x ? this.x : v.x;
    this.y = this.y > v.y ? this.y : v.y;
  }

  Abs() {
    if (this.x < 0) {
      this.x = -this.x;
    }
    if (this.y < 0) {
      this.y = -this.y;
    }
  }

  MulM(matrix) {
    const x = this.x;
    this.x = b2Scalar(matrix.col1.x * x + matrix.col2.x * this.y);
    this.y = b2Scalar(matrix.col1.y * x + matrix.col2.y * this.y);
  }

  MulTM(matrix) {
    const x = b2Math.b2Dot(this, matrix.col1);
    this.y = b2Scalar(b2Math.b2Dot(this, matrix.col2));
    this.x = b2Scalar(x);
  }

  CrossVF(value) {
    const x = this.x;
    this.x = b2Scalar(value * this.y);
    this.y = b2Scalar(-value * x);
  }

  CrossFV(value) {
    const x = this.x;
    this.x = b2Scalar(-value * this.y);
    this.y = b2Scalar(value * x);
  }

  IsValid() {
    return b2Math.b2IsValid(this.x) && b2Math.b2IsValid(this.y);
  }
}

export class b2Mat22 {
  constructor(angle = 0, c1 = null, c2 = null) {
    this.col1 = new b2Vec2();
    this.col2 = new b2Vec2();
    if (c1 != null && c2 != null) {
      this.col1.SetV(c1);
      this.col2.SetV(c2);
    } else {
      const cosine = Math.cos(angle);
      const sine = Math.sin(angle);
      this.col1.x = b2Scalar(cosine);
      this.col2.x = b2Scalar(-sine);
      this.col1.y = b2Scalar(sine);
      this.col2.y = b2Scalar(cosine);
    }
  }

  SetIdentity() {
    this.col1.x = 1;
    this.col2.x = 0;
    this.col1.y = 0;
    this.col2.y = 1;
  }

  SetVV(c1, c2) {
    this.col1.SetV(c1);
    this.col2.SetV(c2);
  }

  Set(angle) {
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    this.col1.x = b2Scalar(cosine);
    this.col2.x = b2Scalar(-sine);
    this.col1.y = b2Scalar(sine);
    this.col2.y = b2Scalar(cosine);
  }

  SetZero() {
    this.col1.x = 0;
    this.col2.x = 0;
    this.col1.y = 0;
    this.col2.y = 0;
  }

  SetM(matrix) {
    this.col1.SetV(matrix.col1);
    this.col2.SetV(matrix.col2);
  }

  AddM(matrix) {
    this.col1.x = b2Scalar(this.col1.x + matrix.col1.x);
    this.col1.y = b2Scalar(this.col1.y + matrix.col1.y);
    this.col2.x = b2Scalar(this.col2.x + matrix.col2.x);
    this.col2.y = b2Scalar(this.col2.y + matrix.col2.y);
  }

  Copy() {
    return new b2Mat22(0, this.col1, this.col2);
  }

  Abs() {
    this.col1.Abs();
    this.col2.Abs();
  }

  Invert(out) {
    const a = this.col1.x;
    const b = this.col2.x;
    const c = this.col1.y;
    const d = this.col2.y;
    const det = b2Scalar(1 / (a * d - b * c));
    out.col1.x = b2Scalar(det * d);
    out.col2.x = b2Scalar(-det * b);
    out.col1.y = b2Scalar(-det * c);
    out.col2.y = b2Scalar(det * a);
    return out;
  }

  Solve(out, x, y) {
    const a11 = this.col1.x;
    const a12 = this.col2.x;
    const a21 = this.col1.y;
    const a22 = this.col2.y;
    const det = b2Scalar(1 / (a11 * a22 - a12 * a21));
    out.x = b2Scalar(det * (a22 * x - a12 * y));
    out.y = b2Scalar(det * (a11 * y - a21 * x));
    return out;
  }

  GetAngle() {
    return Math.atan2(this.col1.y, this.col1.x);
  }
}

export class b2XForm {
  constructor(position = null, rotation = null) {
    this.position = new b2Vec2();
    this.R = new b2Mat22();
    if (position) {
      this.position.SetV(position);
      this.R.SetM(rotation);
    }
  }

  Initialize(position, rotation) {
    this.position.SetV(position);
    this.R.SetM(rotation);
  }

  Set(xf) {
    this.position.SetV(xf.position);
    this.R.SetM(xf.R);
  }

  SetIdentity() {
    this.position.SetZero();
    this.R.SetIdentity();
  }
}

export class b2Sweep {
  constructor() {
    this.localCenter = new b2Vec2();
    this.c = new b2Vec2();
    this.c0 = new b2Vec2();
    this.a = 0;
    this.a0 = 0;
    this.t0 = 0;
  }

  Advance(t) {
    if (this.t0 < t && 1 - this.t0 > Number.MIN_VALUE) {
      const alpha = (t - this.t0) / (1 - this.t0);
      this.c0.x = b2Scalar((1 - alpha) * this.c0.x + alpha * this.c.x);
      this.c0.y = b2Scalar((1 - alpha) * this.c0.y + alpha * this.c.y);
      this.a0 = b2Scalar((1 - alpha) * this.a0 + alpha * this.a);
      this.t0 = b2Scalar(t);
    }
  }

  GetXForm(xf, t) {
    if (1 - this.t0 > Number.MIN_VALUE) {
      const alpha = (t - this.t0) / (1 - this.t0);
      xf.position.x = b2Scalar((1 - alpha) * this.c0.x + alpha * this.c.x);
      xf.position.y = b2Scalar((1 - alpha) * this.c0.y + alpha * this.c.y);
      xf.R.Set(b2Scalar((1 - alpha) * this.a0 + alpha * this.a));
    } else {
      xf.position.SetV(this.c);
      xf.R.Set(this.a);
    }
    const rotation = xf.R;
    xf.position.x = b2Scalar(xf.position.x - (rotation.col1.x * this.localCenter.x + rotation.col2.x * this.localCenter.y));
    xf.position.y = b2Scalar(xf.position.y - (rotation.col1.y * this.localCenter.x + rotation.col2.y * this.localCenter.y));
  }
}

export class b2Math {
  static b2Vec2_zero = new b2Vec2(0, 0);
  static b2Mat22_identity = new b2Mat22(0, new b2Vec2(1, 0), new b2Vec2(0, 1));
  static b2XForm_identity = new b2XForm(b2Math.b2Vec2_zero, b2Math.b2Mat22_identity);

  static b2CrossVF(a, s) {
    return new b2Vec2(b2Scalar(s * a.y), b2Scalar(-s * a.x));
  }

  static AddVV(a, b) {
    return new b2Vec2(b2Scalar(a.x + b.x), b2Scalar(a.y + b.y));
  }

  static b2IsValid(value) {
    return Number.isFinite(value);
  }

  static b2MinV(a, b) {
    return new b2Vec2(b2Math.b2Min(a.x, b.x), b2Math.b2Min(a.y, b.y));
  }

  static b2MulX(xf, v) {
    const out = b2Math.b2MulMV(xf.R, v);
    out.x = b2Scalar(out.x + xf.position.x);
    out.y = b2Scalar(out.y + xf.position.y);
    return out;
  }

  static b2DistanceSquared(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy;
  }

  static b2Swap(a, b) {
    const temp = a[0];
    a[0] = b[0];
    b[0] = temp;
  }

  static b2AbsM(m) {
    return new b2Mat22(0, b2Math.b2AbsV(m.col1), b2Math.b2AbsV(m.col2));
  }

  static SubtractVV(a, b) {
    return new b2Vec2(b2Scalar(a.x - b.x), b2Scalar(a.y - b.y));
  }

  static b2MulXT(xf, v) {
    const out = b2Math.SubtractVV(v, xf.position);
    const x = out.x * xf.R.col1.x + out.y * xf.R.col1.y;
    out.y = b2Scalar(out.x * xf.R.col2.x + out.y * xf.R.col2.y);
    out.x = b2Scalar(x);
    return out;
  }

  static b2Abs(value) {
    return value > 0 ? value : -value;
  }

  static Abs(value) {
    return b2Math.b2Abs(value);
  }

  static b2Clamp(value, low, high) {
    return b2Math.b2Max(low, b2Math.b2Min(value, high));
  }

  static Clamp(value, low, high) {
    return b2Math.b2Clamp(value, low, high);
  }

  static b2AbsV(v) {
    return new b2Vec2(b2Math.b2Abs(v.x), b2Math.b2Abs(v.y));
  }

  static MulFV(s, v) {
    return new b2Vec2(b2Scalar(s * v.x), b2Scalar(s * v.y));
  }

  static b2CrossVV(a, b) {
    return b2Scalar(a.x * b.y - a.y * b.x);
  }

  static b2Dot(a, b) {
    return b2Scalar(a.x * b.x + a.y * b.y);
  }

  static Dot(a, b) {
    return b2Math.b2Dot(a, b);
  }

  static b2CrossFV(s, v) {
    return new b2Vec2(b2Scalar(-s * v.y), b2Scalar(s * v.x));
  }

  static AddMM(a, b) {
    return new b2Mat22(0, b2Math.AddVV(a.col1, b.col1), b2Math.AddVV(a.col2, b.col2));
  }

  static b2Distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  static b2MulTMM(a, b) {
    const c1 = new b2Vec2(b2Math.b2Dot(a.col1, b.col1), b2Math.b2Dot(a.col2, b.col1));
    const c2 = new b2Vec2(b2Math.b2Dot(a.col1, b.col2), b2Math.b2Dot(a.col2, b.col2));
    return new b2Mat22(0, c1, c2);
  }

  static b2MaxV(a, b) {
    return new b2Vec2(b2Math.b2Max(a.x, b.x), b2Math.b2Max(a.y, b.y));
  }

  static b2IsPowerOfTwo(x) {
    return x > 0 && (x & (x - 1)) === 0;
  }

  static b2ClampV(a, low, high) {
    return b2Math.b2MaxV(low, b2Math.b2MinV(a, high));
  }

  static b2RandomRange(low, high) {
    return (high - low) * Math.random() + low;
  }

  static b2MulTMV(m, v) {
    return new b2Vec2(b2Scalar(b2Math.b2Dot(v, m.col1)), b2Scalar(b2Math.b2Dot(v, m.col2)));
  }

  static b2Min(a, b) {
    return a < b ? a : b;
  }

  static b2Random() {
    return Math.random() * 2 - 1;
  }

  static b2MulMM(a, b) {
    return new b2Mat22(0, b2Math.b2MulMV(a, b.col1), b2Math.b2MulMV(a, b.col2));
  }

  static b2NextPowerOfTwo(x) {
    x |= (x >> 1) & 0x7FFFFFFF;
    x |= (x >> 2) & 0x3FFFFFFF;
    x |= (x >> 4) & 0x0FFFFFFF;
    x |= (x >> 8) & 0x00FFFFFF;
    x |= (x >> 16) & 0x0000FFFF;
    return x + 1;
  }

  static b2Max(a, b) {
    return a > b ? a : b;
  }

  static Min(a, b) {
    return b2Math.b2Min(a, b);
  }

  static Max(a, b) {
    return b2Math.b2Max(a, b);
  }

  static b2MulMV(m, v) {
    return new b2Vec2(
      b2Scalar(m.col1.x * v.x + m.col2.x * v.y),
      b2Scalar(m.col1.y * v.x + m.col2.y * v.y),
    );
  }
}
